<style lang="scss">
    .phenotype-view {
        display: flex;
        flex-direction: column;
        &__visualizations {
            width: 100%;
            @include above(lg) {
                flex: 1 1 auto;
                display: flex;
                justify-content: space-around;
            }
        }

        &__sunburst,
        &__scatter {
            flex: 1 1 auto;
            display: flex;
            justify-content: center;
            & > div {
                &:not(.loader) {
                    display: flex;
                    @include above(xs) {
                        flex: 1 1 auto;
                    }
                    & > * {
                        flex: 1 1 auto;
                        @include above(lg) {
                            display: flex;
                        }
                        & > svg {
                            flex: 1 1 auto;
                        }
                    }
                }
            }
        }
        &__sunburst {
            display: none;
            @include above(lg) {
                display: flex;
                flex: 0 1 auto;
                width: 40%;
                &.zoomed {
                    display: none;
                }
            }
        }
        &__scatter  {
            max-width: 500px;
            margin: 0 auto;
            @include above(lg) {
                max-width: initial;
                margin: initial;
                flex: 0 1 auto;
                width: 40%;
                &.zoomed {
                    width: auto;
                }
            }
        }

        &__sunburst-pills {
            max-width: 500px;
            @include _fg(2, 5, 0);

            @include above(xs) {
                margin-left: auto;
                margin-right: auto;
            }

            @include above(sm) {
                @include _fg(3, 5, 0);
                margin-left: auto;
                margin-right: auto;
            }

            @include above(lg) {
                display: none;
            }

            &-pill {
                display: flex;
                align-items: center;
                justify-content: center;
                text-align: center;
                padding: 10px;
                border-radius: 25px;
                color: $black;
                margin-bottom: 5px;
            }
        }

        &__zoom {
            display: none;
            position: absolute;
            bottom: 70px;
            left: 20px;
            background-image: url('/img/icons/zoom-in-white.png');
            background-position: center;
            background-repeat: no-repeat;
            background-size: contain;
            width: 30px;
            height: 30px;
            opacity: 0.7;

            @include above(lg) {
                display: block;
            }

            &.zoomed {
                background-image: url('/img/icons/zoom-out-white.png');
            }
        }
    }
</style>
<template>
    <div class="phenotype-view" v-if="progressData.length && species && progress">
        <neon-box
            :color="progressData[progress].color"
            :image="tissue.images.poly"
            :downloads="tissue.downloads"
            :citations="tissue.citations"
            :back-link="{name: 'TissueView', params: {species: speciesKey}}"
        >
            <div class="phenotype-view__visualizations">
                <div class="phenotype-view__scatter" :class="{zoomed}">
                    <scatterplot
                        v-if="!dataLoading && !hierarchyLoading && phenotypeHierarchy && phenotypeData"
                        :hierarchy="phenotypeHierarchy"
                        :data="phenotypeData"
                        x-label="tSNE X"
                        y-label="tSNE Y"
                        @click="goToSubset"
                    />
                    <loading class="loader" v-else
                />
                </div>
                <div class="phenotype-view__sunburst" :class="{zoomed}">
                    <sunburst
                        v-if="!hierarchyLoading && phenotypeHierarchy"
                        :hierarchy="phenotypeHierarchy"
                        @click="goToSubset"
                    />
                    <loading class="loader" v-else />
                </div>
                <div class="phenotype-view__sunburst-pills">
                    <router-link
                        class="phenotype-view__sunburst-pills-pill"
                        v-for="(phenotype, i) in phenotypeHierarchy ? phenotypeHierarchy.children : []"
                        :key="`${phenotype.key}-${i}`"
                        :style="{backgroundColor: phenotype.color}"
                        :to="{name: 'SubsetView', params: {phenotype: phenotype.key}}"
                    >
                        {{ phenotype.name }}
                    </router-link>
                </div>
            </div>
            <a href="#!" @click.prevent="zoomed=!zoomed" class="phenotype-view__zoom" :class="{zoomed}"></a>
        </neon-box>
    </div>
</template>

<script>
import Sunburst from '@/components/Sunburst'
import TitleMixin from '@/mixins/title-mixin'

const NeonBox = () => import(/* webpackChunkName 'NeonBox' */ '@/components/NeonBox')
const Scatterplot = () => import(/* webpackChunkName 'Scatterplot' */ '@/components/Scatterplot')
const Loading = () => import(/* webpackChunkName 'Loading' */ '@/components/Loading')

import { mapState, mapMutations, mapActions } from 'vuex'

export default {
    mixins: [TitleMixin],
    name: 'PhenotypeView',
    title() {
        return this.species && this.tissue ? `${this.species.name} > ${this.tissue.name}` : null
    },
    data: () => ({
        dataFetchHandle: null,
        hierarchyLoading: false,
        dataLoading: false,
        zoomed: false,
    }),
    computed: {
        speciesKey() {
            return this.$route.params.species
        },
        tissueKey() {
            return this.$route.params.tissue
        },
        tissue() {
            return this.species ? this.species.tissues.reduce((sum, tissue) => this.tissueKey === tissue.key ? tissue : sum, null) : null
        },
        ...mapState({
            species(state) {
                return state.species.reduce((sum, species) => this.speciesKey === species.key ? species : sum, null)
            },
            progress: 'progress',
            progressData: 'progressData',
            phenotypeData: 'phenotypeData',
            phenotypeHierarchy(state) {
                if (!state.phenotypeHierarchy) {
                    return null
                }
                return this.filterUnknownFromHierarchy(state.phenotypeHierarchy)
            },
        }),
    },
    methods: {
        filterUnknownFromHierarchy(hierarchy) {
            return {
                ...hierarchy,
                ...(hierarchy.children ? {children: this.filterUnknownFromChildren(hierarchy.children)} : {}),
            }
        },
        filterUnknownFromChildren(children) {
            return children
                .filter(c => c.key !== 'unknown')
                .map(c => this.filterUnknownFromHierarchy(c))
        },
        goToSubset(phenotype) {
            this.$router.push({name: 'SubsetView', params: {...this.$route.params, phenotype}})
        },
        ...mapActions([
            'getPhenotypeJsonData',
            'getPhenotypeCsvData',
        ]),
        ...mapMutations([
            'setPhenotypeData',
            'setPhenotypeHierarchy',
            'setNeonTitle',
            'setProgressLinks',
            'setProgressTitles',
        ]),
    },
    beforeMount() {
        this.setPhenotypeData(null)
        this.setPhenotypeHierarchy(null)
    },
    mounted() {
        if (this.species && this.tissue) {
            clearTimeout(this.dataFetchHandle)
            this.dataFetchHandle = setTimeout(() => {
                if (!this.phenotypeHierarchy) {
                    this.hierarchyLoading = true
                    this.getPhenotypeJsonData(this.tissue.phenotypes).then(() => {
                        this.hierarchyLoading = false
                    })
                }
                if (!this.phenotypeData) {
                    this.dataLoading = true
                    this.getPhenotypeCsvData({folder: this.tissue.phenotypes.folder, types: ['merged']}).then(() => {
                        this.dataLoading = false
                    })
                }
            }, 750)
            this.setProgressTitles([
                this.species.name,
                this.tissue.name,
            ])
            this.setProgressLinks([
                {name: 'SpeciesView'},
                {name: 'TissueView', params: {species: this.speciesKey}},
            ])
            this.setNeonTitle(this.tissue.name)
        }
    },
    updated() {
        if (this.species && this.tissue) {
            clearTimeout(this.dataFetchHandle)
            this.dataFetchHandle = setTimeout(() => {
                if (!this.phenotypeHierarchy) {
                    this.hierarchyLoading = true
                    this.getPhenotypeJsonData(this.tissue.phenotypes).then(() => {
                        this.hierarchyLoading = false
                    })
                }
                if (!this.phenotypeData) {
                    this.dataLoading = true
                    this.getPhenotypeCsvData({folder: this.tissue.phenotypes.folder, types: ['merged']}).then(() => {
                        this.dataLoading = false
                    })
                }
            }, 750)
            this.setProgressTitles([
                this.species.name,
                this.tissue.name,
            ])
            this.setProgressLinks([
                {name: 'SpeciesView'},
                {name: 'TissueView', params: {species: this.speciesKey}},
            ])
            this.setNeonTitle(this.tissue.name)
        }
    },
    components: {
        NeonBox,
        Sunburst,
        Scatterplot,
        Loading,
    },
}
</script>
